import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import axios from "axios";
import { Mail, MapPin, Send, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    document.title = "Contact Donald Stephen Inbody — Author & Researcher";
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Name must be at least 2 characters.";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.subject.trim()) e.subject = "Subject is required.";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Message must be at least 10 characters.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    setApiError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
    } catch (err) {
      setApiError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErrors(er => ({ ...er, [e.target.name]: undefined }));
  };

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="Contact Donald Stephen Inbody"
        description="Contact Donald Stephen Inbody — author, naval historian, and political scientist. Reach out for speaking engagements, media inquiries, or general correspondence."
        canonical="https://inbody.net/contact"
        keywords="contact Donald Stephen Inbody, speaking engagements, media inquiries, author contact"
      />
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-16 border-b border-[#0A1628]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline mb-3">Get in Touch</p>
          <h1 data-testid="contact-page-title" className="font-serif text-5xl font-black text-[#0A1628] mb-4">
            Contact
          </h1>
          <p className="text-[#2D3142] max-w-xl leading-relaxed">
            For press inquiries, speaking engagements, academic collaboration, radio contacts, or general correspondence.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">

            {/* Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <p className="overline mb-4">Reach Out For</p>
                <ul className="space-y-3">
                  {[
                    "Press & media inquiries",
                    "Speaking engagements",
                    "Academic collaboration",
                    "Amateur radio (ADØK) contacts",
                    "Music & booking inquiries",
                    "Reader correspondence",
                  ].map(item => (
                    <li key={item} className="flex items-start gap-3 text-sm text-[#2D3142]">
                      <span className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-[#0A1628]/10 pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#0A1628] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail size={14} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-0.5">Email</div>
                    <div className="text-sm font-medium text-[#0A1628]">Via contact form</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#0A1628] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin size={14} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-0.5">Location</div>
                    <div className="text-sm font-medium text-[#0A1628]">United States</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {success ? (
                <div data-testid="contact-success" className="flex flex-col items-center justify-center py-16 text-center border border-[#0A1628]/12 bg-white">
                  <CheckCircle size={40} className="text-green-500 mb-4" />
                  <h3 className="font-serif text-2xl font-bold text-[#0A1628] mb-2">Message Sent</h3>
                  <p className="text-[#2D3142] leading-relaxed max-w-sm">
                    Thank you for reaching out. I'll review your message and respond as soon as possible.
                  </p>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="bg-white border border-[#0A1628]/10 p-8"
                  noValidate
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2" htmlFor="name">
                        Full Name *
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        data-testid="contact-name"
                        className={`w-full border px-4 py-3 text-sm bg-[#F5F0E8] text-[#0A1628] focus:outline-none focus:border-[#0A1628] transition-colors ${errors.name ? "border-red-400" : "border-[#0A1628]/20"}`}
                        placeholder="Your full name"
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2" htmlFor="email">
                        Email Address *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        data-testid="contact-email"
                        className={`w-full border px-4 py-3 text-sm bg-[#F5F0E8] text-[#0A1628] focus:outline-none focus:border-[#0A1628] transition-colors ${errors.email ? "border-red-400" : "border-[#0A1628]/20"}`}
                        placeholder="your@email.com"
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2" htmlFor="subject">
                      Subject *
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      value={form.subject}
                      onChange={handleChange}
                      data-testid="contact-subject"
                      className={`w-full border px-4 py-3 text-sm bg-[#F5F0E8] text-[#0A1628] focus:outline-none focus:border-[#0A1628] transition-colors ${errors.subject ? "border-red-400" : "border-[#0A1628]/20"}`}
                      placeholder="What is this regarding?"
                    />
                    {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                  </div>
                  <div className="mb-8">
                    <label className="block text-xs uppercase tracking-widest text-[#2D3142]/60 mb-2" htmlFor="message">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={form.message}
                      onChange={handleChange}
                      data-testid="contact-message"
                      className={`w-full border px-4 py-3 text-sm bg-[#F5F0E8] text-[#0A1628] focus:outline-none focus:border-[#0A1628] transition-colors resize-none ${errors.message ? "border-red-400" : "border-[#0A1628]/20"}`}
                      placeholder="Write your message here..."
                    />
                    {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                  </div>
                  {apiError && (
                    <p data-testid="contact-api-error" className="text-red-500 text-sm mb-4">{apiError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-8 py-3 text-sm font-semibold hover:bg-[#2D3142] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : <><Send size={14} /> Send Message</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
